import RegistrationPasswordFields, {passwordIssues} from './RegistrationPasswordFields';
import {useRegistrationLegal} from './useRegistrationLegal';
import RegistrationConsent from './RegistrationConsent';
import {FormEvent, useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, CircularProgress, Stack, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import RegistrationLayout from './RegistrationLayout';
import {InviteValidationResult} from '../../models/IRegistration';
import {registerWithInvite, validateRegistrationInvite} from '../../services/RegistrationInviteClient';
import {normalizeApiError} from '../../common/apiErrors';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';

type Status = 'checking' | 'valid' | 'invalid' | 'unavailable' | 'success';

export default function InviteRegistrationPage() {
    const {t, i18n} = useAppTranslation();
    const legal = useRegistrationLegal();
    const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
    const [status, setStatus] = useState<Status>(token ? 'checking' : 'invalid');
    const [invite, setInvite] = useState<InviteValidationResult>();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const validationStarted = useRef(false);

    useEffect(() => {
        if (window.location.hash) window.history.replaceState(null, '', window.location.pathname + window.location.search);
        if (!token || validationStarted.current) return;
        validationStarted.current = true;
        validateRegistrationInvite(token).then(result => {
            setInvite(result);
            setStatus('valid');
        }).catch(error => {
            setStatus(normalizeApiError(error).code === 'registration.unavailable' ? 'unavailable' : 'invalid');
        });
    }, [token]);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!legal.accepted) return;
        if (password !== confirmPassword) return setErrorMessage(String(t('registration:password_mismatch')));
        if (passwordIssues(password).length) return setErrorMessage(String(t('registration:password_invalid')));
        setIsSubmitting(true);
        setErrorMessage('');
        try {
            await registerWithInvite(token, password, confirmPassword, legal.actions);
            setPassword('');
            setConfirmPassword('');
            setStatus('success');
        } catch (error) {
            if (legal.handleError(error)) return;
            const problem = normalizeApiError(error);
            if (problem.status === 429) setErrorMessage(String(t('registration:rate_limited')));
            else if (problem.code === 'registration_invite.invalid') setStatus('invalid');
            else if (problem.code === 'registration.unavailable') setStatus('unavailable');
            else setErrorMessage(String(t('registration:registration_rejected')));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (status === 'checking') return <RegistrationLayout><Box sx={{py: 8, textAlign: 'center'}}><CircularProgress/><Typography sx={{mt: 2}}>{t('registration:invite_checking')}</Typography></Box></RegistrationLayout>;
    if (status === 'invalid') return <RegistrationLayout><State title={t('registration:invite_invalid_title')} body={t('registration:invite_invalid_body')}/></RegistrationLayout>;
    if (status === 'unavailable') return <RegistrationLayout><State title={t('registration:closed_title')} body={t('registration:registration_unavailable')}/></RegistrationLayout>;
    if (status === 'success') return <RegistrationLayout><State title={t('registration:account_created_title')} body={t('registration:account_created_body')}><Button component={RouterLink} to="/login" variant="contained">{t('registration:go_to_login')}</Button></State></RegistrationLayout>;

    return <RegistrationLayout>
        <Typography component="h1" variant="h3" sx={{mt: 1}}>{t('registration:invite_title')}</Typography>
        <Typography color="text.secondary" sx={{mt: 1}}>{String(t('registration:invite_email' as never, {email: invite?.maskedEmail} as never))}</Typography>
        {invite?.expiresAtUtc && <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, mb: 4}}>{String(t('registration:invite_expires' as never, {date: new Intl.DateTimeFormat(i18n.language, {dateStyle: 'medium', timeStyle: 'short'}).format(new Date(invite.expiresAtUtc))} as never))}</Typography>}
        {!!errorMessage && <Alert severity="error" sx={{my: 2}}>{errorMessage}</Alert>}
        <Stack component="form" onSubmit={submit} spacing={2.5} sx={{mt: 3}}>
            <RegistrationPasswordFields password={password} confirmPassword={confirmPassword}
                onPasswordChange={setPassword} onConfirmPasswordChange={setConfirmPassword}/>
            <RegistrationConsent legal={legal}/>
            <Button type="submit" size="large" variant="contained" disabled={isSubmitting || !legal.accepted}>{isSubmitting ? t('registration:creating_account') : t('registration:create_account')}</Button>
        </Stack>
    </RegistrationLayout>;
}

function State({title, body, children}: {title: string; body: string; children?: React.ReactNode}) {
    return <Stack spacing={2.5} sx={{py: 4}}><Typography component="h1" variant="h3">{title}</Typography><Typography color="text.secondary">{body}</Typography>{children}</Stack>;
}
